import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch } from '../hooks'
import { updatePassword } from '../store/auth.slice'
import { NavBack } from '../components/nav-back'
import { Spinner } from '../components/spinner'
import { Status } from '../components/status'
import { useUrlAuth } from '../utils/useUrlAuth'
import { ClientAuthDto } from '../utils/types/auth'

export const ResetPasswordView = () => {
  const [passwords, setPasswords] = useState({
    password: '',
    confirm_password: ''
  })
  const [isPasswordVisible, setIsPasswordVisible] = useState({
    new: false,
    confirm: false
  })
  const [isLoading, setIsLoading] = useState(false)
  const [requestState, setRequestState] = useState({ success: '', error: '' })
  const inputRef = useRef<HTMLInputElement>(null)

  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { urlAuth } = useUrlAuth()

  const isRecoveringAccount = urlAuth.get('type') === 'recovery'
  let errorMessage = ''

  if (urlAuth.get('error')) {
    if (urlAuth.get('error_code') === 'otp_expired') {
      errorMessage = 'Password reset link expired'
    } else {
      errorMessage = urlAuth.get('error_description')
      errorMessage = errorMessage.replaceAll('+', ' ')
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setPasswords({ ...passwords, [e.target.id]: e.target.value })

  const getIsPasswordInputValid = (): boolean =>
    !!(passwords.password && passwords.confirm_password) && !requestState.success

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const payload: { password: string; clientAuth?: ClientAuthDto } = {
      password: passwords.password
    }

    if (isRecoveringAccount) {
      payload.clientAuth = {
        access_token: urlAuth.get('access_token'),
        refresh_token: urlAuth.get('refresh_token'),
        expires_at: urlAuth.get('expires_at'),
        expires_in: urlAuth.get('expires_in')
      }
    }

    try {
      setIsLoading(true)

      const { status } = await dispatch(updatePassword(payload)).unwrap()
      if (status === 200) {
        setRequestState({ success: 'Password updated', error: '' })
      }
    } catch (error: any) {
      setRequestState({ success: '', error: error.message })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className='main_content experience_content experience_content-alt'>
      <NavBack />
      {requestState.success && <Status success={requestState.success} />}
      {requestState.error && (
        <Status
          error={requestState.error}
          reset={() => setRequestState({ ...requestState, error: '' })}
        />
      )}
      <div>
        <h1 className='heading-styled'>
          {isRecoveringAccount ? 'Finish recovering your account...' : 'Reset password...'}
        </h1>
        {errorMessage ? (
          <>
            <Status error={errorMessage} />
            <span
              className='span-a'
              onClick={() => navigate('/recover-account', { viewTransition: true })}>
              Retry
            </span>
          </>
        ) : (
          <>
            <p>
              Enter and confirm your <em>new password.</em>
            </p>
            <form onSubmit={handleSubmit} className='form-grid'>
              <label htmlFor='password'>New password:</label>
              <div className='input_container'>
                <input
                  id='password'
                  type={`${isPasswordVisible.new ? 'text' : 'password'}`}
                  onChange={handleChange}
                  required
                  readOnly={!!requestState.success}
                  ref={inputRef}
                />
                <span
                  className='material-symbols-rounded'
                  onClick={() =>
                    setIsPasswordVisible({
                      ...isPasswordVisible,
                      new: !isPasswordVisible.new
                    })
                  }>
                  {isPasswordVisible.new ? 'visibility_off' : 'visibility'}
                </span>
              </div>
              <label htmlFor='confirm_password'>Confirm password:</label>
              <div className='input_container'>
                <input
                  id='confirm_password'
                  type={`${isPasswordVisible.confirm ? 'text' : 'password'}`}
                  onChange={handleChange}
                  required
                  readOnly={!!requestState.success}
                  ref={inputRef}
                />
                <span
                  className='material-symbols-rounded'
                  onClick={() =>
                    setIsPasswordVisible({
                      ...isPasswordVisible,
                      confirm: !isPasswordVisible.confirm
                    })
                  }>
                  {isPasswordVisible.confirm ? 'visibility_off' : 'visibility'}
                </span>
              </div>
              {getIsPasswordInputValid() && (
                <button
                  className='submit'
                  disabled={passwords.password !== passwords.confirm_password || isLoading}>
                  {isLoading ? <Spinner /> : <span>Update password</span>}
                </button>
              )}
            </form>
            {requestState.success && isRecoveringAccount && (
              <span
                className='span-a'
                onClick={() => navigate('/log-in', { viewTransition: true })}>
                Log in to account
              </span>
            )}
          </>
        )}
      </div>
    </div>
  )
}
